import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { RichTextEditor } from '../components/RichTextEditor';
import { getUser, getToken } from '../utils/auth';
import { toast } from 'sonner';
import api from '../utils/api';

export default function JobEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = getUser();
  const token = getToken();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    company: '',
    location: '',
    description: '',
    requirements: '',
    job_type: 'full-time',
    experience_level: 'entry',
    category: '',
    salary_min: '',
    salary_max: '',
    benefits: '',
    skills: ''
  });

  useEffect(() => {
    if (!token || !user || user.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    if (id) {
      fetchJob();
    } else {
      // Check for draft
      checkForDraft();
    }
  }, [id]);

  const checkForDraft = () => {
    const descDraft = localStorage.getItem('draft_job_description');
    const reqDraft = localStorage.getItem('draft_job_requirements');
    if (descDraft || reqDraft) {
      setHasDraft(true);
    }
  };

  const clearDrafts = () => {
    localStorage.removeItem('draft_job_description');
    localStorage.removeItem('draft_job_requirements');
    setHasDraft(false);
    toast.success('Drafts cleared');
  };

  const fetchJob = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/jobs/${id}`);
      const job = response.data;
      setFormData({
        title: job.title || '',
        company: job.company || '',
        location: job.location || '',
        description: job.description || '',
        requirements: job.requirements || '',
        job_type: job.job_type || 'full-time',
        experience_level: job.experience_level || 'entry',
        category: job.category || '',
        salary_min: job.salary_min || '',
        salary_max: job.salary_max || '',
        benefits: Array.isArray(job.benefits) ? job.benefits.join('\n') : '',
        skills: Array.isArray(job.skills) ? job.skills.join(', ') : ''
      });
    } catch (error) {
      toast.error('Failed to fetch job');
      navigate('/dashboard/admin');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const payload = {
        ...formData,
        salary_min: formData.salary_min ? parseFloat(formData.salary_min) : null,
        salary_max: formData.salary_max ? parseFloat(formData.salary_max) : null,
        benefits: formData.benefits ? formData.benefits.split('\n').filter(b => b.trim()) : [],
        skills: formData.skills ? formData.skills.split(',').map(s => s.trim()).filter(s => s) : []
      };
      
      if (id) {
        await api.put(`/jobs/${id}`, payload);
        toast.success('Job updated successfully');
      } else {
        await api.post('/jobs', payload);
        toast.success('Job created successfully');
        // Clear drafts after successful creation
        clearDrafts();
      }
      navigate('/dashboard/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save job');
    } finally {
      setSaving(false);
    }
  };

  const handleAutoSave = () => {
    // Visual feedback could be added here
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="text-lg text-zinc-500">Loading job...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="job-editor-page">
      {/* Header */}
      <div className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard/admin')}
                className="text-zinc-600 hover:text-zinc-900"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-zinc-900">
                  {id ? 'Edit Job' : 'Create New Job'}
                </h1>
                <p className="text-sm text-zinc-500">
                  {id ? 'Update job details' : 'Fill in the job details below'}
                </p>
              </div>
            </div>
            {hasDraft && !id && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={clearDrafts}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Clear Drafts
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information Card */}
          <div className="bg-white rounded-2xl p-6 sm:p-8 border border-zinc-200 shadow-sm">
            <h2 className="text-lg font-semibold text-zinc-900 mb-6 pb-4 border-b border-zinc-100">
              Basic Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-zinc-700 mb-2">
                  Job Title <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g. Senior Software Engineer"
                  className="h-12"
                  data-testid="job-title-input"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-zinc-700 mb-2">
                  Company <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="e.g. Can X Global"
                  className="h-12"
                  data-testid="company-input"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-zinc-700 mb-2">
                  Location <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="e.g. Toronto, ON"
                  className="h-12"
                  data-testid="location-input"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-zinc-700 mb-2">Category</label>
                <Input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="e.g. Technology, Healthcare"
                  className="h-12"
                  data-testid="category-input"
                />
              </div>
            </div>
          </div>

          {/* Job Type & Experience Card */}
          <div className="bg-white rounded-2xl p-6 sm:p-8 border border-zinc-200 shadow-sm">
            <h2 className="text-lg font-semibold text-zinc-900 mb-6 pb-4 border-b border-zinc-100">
              Job Type & Compensation
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-zinc-700 mb-2">Job Type</label>
                <select
                  value={formData.job_type}
                  onChange={(e) => setFormData({ ...formData, job_type: e.target.value })}
                  className="w-full h-12 rounded-lg border border-zinc-200 px-4 bg-white focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue"
                  data-testid="job-type-select"
                >
                  <option value="full-time">Full Time</option>
                  <option value="part-time">Part Time</option>
                  <option value="contract">Contract</option>
                  <option value="remote">Remote</option>
                  <option value="internship">Internship</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-zinc-700 mb-2">Experience Level</label>
                <select
                  value={formData.experience_level}
                  onChange={(e) => setFormData({ ...formData, experience_level: e.target.value })}
                  className="w-full h-12 rounded-lg border border-zinc-200 px-4 bg-white focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue"
                  data-testid="experience-level-select"
                >
                  <option value="entry">Entry Level</option>
                  <option value="mid">Mid Level</option>
                  <option value="senior">Senior Level</option>
                  <option value="executive">Executive</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-zinc-700 mb-2">
                  Salary Min <span className="text-zinc-400 font-normal">(hourly or annual)</span>
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.salary_min}
                  onChange={(e) => setFormData({ ...formData, salary_min: e.target.value })}
                  placeholder="e.g. 25 (hourly) or 50000 (annual)"
                  className="h-12"
                  data-testid="salary-min-input"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-zinc-700 mb-2">Salary Max</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.salary_max}
                  onChange={(e) => setFormData({ ...formData, salary_max: e.target.value })}
                  placeholder="e.g. 35 (hourly) or 80000 (annual)"
                  className="h-12"
                  data-testid="salary-max-input"
                />
              </div>
            </div>
          </div>

          {/* Job Description Card */}
          <div className="bg-white rounded-2xl p-6 sm:p-8 border border-zinc-200 shadow-sm">
            <h2 className="text-lg font-semibold text-zinc-900 mb-2">
              Job Description <span className="text-red-500">*</span>
            </h2>
            <p className="text-sm text-zinc-500 mb-6">
              Write a compelling description of the role. Use formatting to highlight key points.
            </p>
            <RichTextEditor
              value={formData.description}
              onChange={(value) => setFormData({ ...formData, description: value })}
              placeholder="Describe the role, responsibilities, and what makes this opportunity exciting..."
              maxChars={5000}
              draftKey={id ? null : 'job_description'}
              onAutoSave={handleAutoSave}
            />
          </div>

          {/* Requirements Card */}
          <div className="bg-white rounded-2xl p-6 sm:p-8 border border-zinc-200 shadow-sm">
            <h2 className="text-lg font-semibold text-zinc-900 mb-2">Requirements</h2>
            <p className="text-sm text-zinc-500 mb-6">
              List the qualifications, skills, and experience needed for this role.
            </p>
            <RichTextEditor
              value={formData.requirements}
              onChange={(value) => setFormData({ ...formData, requirements: value })}
              placeholder="List required qualifications, education, certifications, etc..."
              maxChars={3000}
              draftKey={id ? null : 'job_requirements'}
              onAutoSave={handleAutoSave}
            />
          </div>

          {/* Skills & Benefits Card */}
          <div className="bg-white rounded-2xl p-6 sm:p-8 border border-zinc-200 shadow-sm">
            <h2 className="text-lg font-semibold text-zinc-900 mb-6 pb-4 border-b border-zinc-100">
              Skills & Benefits
            </h2>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-zinc-700 mb-2">
                  Skills <span className="text-zinc-400 font-normal">(comma separated)</span>
                </label>
                <Input
                  type="text"
                  value={formData.skills}
                  onChange={(e) => setFormData({ ...formData, skills: e.target.value })}
                  placeholder="e.g. JavaScript, React, Node.js, MongoDB"
                  className="h-12"
                  data-testid="skills-input"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-zinc-700 mb-2">
                  Benefits <span className="text-zinc-400 font-normal">(one per line)</span>
                </label>
                <textarea
                  value={formData.benefits}
                  onChange={(e) => setFormData({ ...formData, benefits: e.target.value })}
                  placeholder="Health Insurance&#10;401k Matching&#10;Flexible Hours&#10;Remote Work Options"
                  className="w-full h-40 rounded-lg border border-zinc-200 px-4 py-3 resize-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue"
                  data-testid="benefits-textarea"
                />
              </div>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex items-center justify-between bg-white rounded-2xl p-6 border border-zinc-200 shadow-sm">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/dashboard/admin')}
              className="px-6 py-3"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="px-8 py-3 bg-brand-blue hover:bg-brand-blue/90 text-white font-semibold"
              data-testid="submit-job-btn"
            >
              <Save className="w-5 h-5 mr-2" />
              {saving ? 'Saving...' : (id ? 'Update Job' : 'Create Job')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
